import { Typography, useEventCallback } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { Fragment, ReactElement, useMemo, useRef } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Filter from 'front-office/components/electoral-card/filter';
import TableTableElectoralCard from 'front-office/components/electoral-card/table';
import { validationSchemaFilter } from 'front-office/constants/electoral-card-schema';
import {
  FiltersFormTableElectoralCard,
  TableElectoralCardBody,
} from 'front-office/view-models/electoral-card';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __ELECTORAL_API_URL__ } from 'shell/constants/environment';
import { HttpResponseListPayloadQuarkus } from 'shell/models/http-response';
import { ResponseTableElectoralCardData } from 'shell/models/electoral-card';
import {
  transformDataset,
  transformerTableElectoralCardData,
} from 'front-office/utils/transformer-electoral-card';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { DateTime } from 'luxon';
import { ElectoralCardTableCrumbs } from 'front-office/constants/electoral-card-table-crumbs';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

const ElectoralCardPage = (): ReactElement => {
  const paginationCount = useRef<number>(0);

  const [list, getList] = useHttpFetch<
    HttpResponseListPayloadQuarkus<ResponseTableElectoralCardData>,
    Array<TableElectoralCardBody>
  >(`${__ELECTORAL_API_URL__}/tessere-elettorali`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: (response) => {
      paginationCount.current = response.data?.pageInfo?.totalCount || 0;
      return transformerTableElectoralCardData(response);
    },
  });

  const { userProfile } = useUserProfile();

  const initialValuesFilter: FiltersFormTableElectoralCard = useMemo(() => {
    const codeControlloCassa = userProfile.datiLogin?.profile?.feature?.controlloDiCassa || '';

    const isEnabledComboService = !(
      codeControlloCassa === '0' ||
      codeControlloCassa === '1' ||
      !codeControlloCassa
    );

    const isEnabledComboOperator = !(codeControlloCassa === '0' || !codeControlloCassa);

    return {
      dateFrom: DateTime.now(),
      dateTo: DateTime.now(),
      page: 0,
      rowsPerPage: 10,
      service: isEnabledComboService
        ? ''
        : userProfile.datiLogin?.profile?.servizio?.descrizione || '', // fixed
      operator: isEnabledComboOperator ? '' : userProfile.datiLogin?.nome || '', // fixed
      serviceCode: isEnabledComboService
        ? ''
        : userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio || '',
      operatorCode: isEnabledComboOperator ? '' : userProfile.datiLogin?.key?.userName || '',
    };
  }, [userProfile.datiLogin]);

  const getDataList = useEventCallback((values: FiltersFormTableElectoralCard) => {
    const queryString = transformDataset(values);

    return new Promise<void>((resolve, reject) => {
      (
        getList({ params: {}, paramsSerializer: () => queryString }) as ResponsePromise<
          HttpResponseListPayloadQuarkus<ResponseTableElectoralCardData>,
          Array<TableElectoralCardBody>
        >
      )
        .then(() => {
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  });

  const onSubmitFilter = useEventCallback(
    (
      values: FiltersFormTableElectoralCard,
      formikContext: FormikHelpers<FiltersFormTableElectoralCard>
    ) => {
      getDataList(values).then(() => {
        formikContext.setValues({
          ...values,
          page: 0,
          rowsPerPage: 10,
        });
      });
    }
  );

  return (
    <Fragment>
      <Breadcrumbs items={ElectoralCardTableCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Lista Tessere Rilasciate
        </Typography>
      </div>
      <Formik
        validationSchema={validationSchemaFilter}
        initialValues={initialValuesFilter}
        onSubmit={onSubmitFilter}
      >
        <Form>
          <Filter />
          <TableTableElectoralCard
            data={list.data as Array<TableElectoralCardBody>}
            count={paginationCount.current}
            getData={getDataList}
          />
        </Form>
      </Formik>
    </Fragment>
  );
};

ElectoralCardPage.displayName = 'Electoral Card List Page component';

export default ElectoralCardPage;
